import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { EmailPreviewComponent } from '../email-preview/email-preview.component';
import { EmailPreviewService, EmailData } from '../../services/email-preview.service';

/**
 * Displays a full-page preview of an email using data provided by EmailPreviewService; redirects to the campaign editor if no data is available and revokes any blob object URLs on destroy.
 * Key methods and lifecycle hooks:
 *   - constructor(emailPreviewService: EmailPreviewService, router: Router): injects services to obtain preview data and perform navigation.
 *   - ngOnInit(): retrieves EmailData from EmailPreviewService and navigates to '/campaign-editor' when no data is present.
 *   - ngOnDestroy(): cleans up any created blob: object URLs (logoUrl, imageUrl) to prevent memory leaks.
 * @component
 * @selector app-email-preview-page
 * @example
 *   <app-email-preview-page></app-email-preview-page>
 * @input {none} none - This component does not accept @Input properties; it sources its data from EmailPreviewService.
 * @output {none} none - This component does not emit @Output events; interactions and navigation are handled internally.
 */
@Component({
  selector: 'app-email-preview-page',
  standalone: true,
  imports: [CommonModule, EmailPreviewComponent],
  templateUrl: './email-preview-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./email-preview-page.component.scss'],
})
export class EmailPreviewPageComponent implements OnInit, OnDestroy {
  emailData: EmailData | null = null;

  constructor(
    private emailPreviewService: EmailPreviewService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.emailData = this.emailPreviewService.getEmailData();

    // If no email data, redirect back
    if (!this.emailData) {
      this.router.navigate(['/campaign-editor']);
    }
  }

  ngOnDestroy(): void {
    // Clean up any object URLs if they were created
    if (this.emailData) {
      if (this.emailData.logoUrl && this.emailData.logoUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.emailData.logoUrl);
      }
      if (this.emailData.imageUrl && this.emailData.imageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.emailData.imageUrl);
      }
    }
  }
}
